import moment from "moment";

/**
 * Draft scheduler events/shifts with no assigned staff members.
 * @param {object | null | undefined} event
 * @returns {boolean}
 */
export const isUnassignedSchedulerEvent = (event) => {
  if (!event) return false;

  // Prefer concrete employee rows from event details when present.
  const activeEmployees = (event?.eventEmployees || []).filter(
    (entry) => !entry?.isDeletedForThisSchedulerEvent,
  );
  if (activeEmployees.length > 0) return false;

  const employeeCount =
    event?.modifiedEmployeeCount ?? event?.eventEmployeeCount ?? 0;
  if (employeeCount > 0) return false;

  return (
    Boolean(event.isDraft) ||
    event.userId == null ||
    Number(event.userId) === 0
  );
};

/**
 * @param {Array<object>} events
 * @returns {Array<object>}
 */
export const sortSchedulerEventsByStartTime = (events = []) =>
  [...events].sort((a, b) => {
    const aTime = a?.startTime ? moment(a.startTime).valueOf() : 0;
    const bTime = b?.startTime ? moment(b.startTime).valueOf() : 0;
    return aTime - bTime;
  });

/**
 * @param {Array<object>} events
 * @returns {{ unassigned: Array<object>, assigned: Array<object> }}
 */
export const partitionSchedulerEvents = (events = []) => {
  const unassigned = [];
  const assigned = [];

  events.forEach((event) => {
    if (isUnassignedSchedulerEvent(event)) {
      unassigned.push(event);
    } else {
      assigned.push(event);
    }
  });

  return {
    unassigned: sortSchedulerEventsByStartTime(unassigned),
    assigned,
  };
};

/**
 * @param {Array<{ schedulerEvents?: Array<object>, [key: string]: unknown }>} weekData
 * @returns {Array<object>}
 */
export const partitionWeekSchedulerData = (weekData = []) =>
  weekData.map((day) => {
    const { unassigned, assigned } = partitionSchedulerEvents(day.schedulerEvents);
    return {
      ...day,
      unassignedEvents: unassigned,
      schedulerEvents: assigned,
    };
  });

/**
 * Unassigned events for one day from `ListForAssignerByEmployee`
 * (`user: null` bucket in `userEventListForAssignerList`).
 * @param {object | null | undefined} day
 * @returns {Array<object>}
 */
export const getUnassignedEventsFromAssignerByEmployeeDay = (day) => {
  const grouped = day?.userEventListForAssignerList;
  if (Array.isArray(grouped)) {
    const unassignedEntry = grouped.find(
      (entry) => entry?.user == null || !entry?.user?.id,
    );
    return unassignedEntry?.schedulerEvents || [];
  }

  return (day?.schedulerEvents || []).filter(isUnassignedSchedulerEvent);
};

/**
 * Builds per-day unassigned event lists from `ListForAssignerByEmployee` response.
 * @param {Array<{ requestedDate?: string, date?: string, userEventListForAssignerList?: Array }>} assignerDays
 * @param {Array<{ date?: string }>} weekDays
 * @returns {Array<{ date: string, dateStr: string, events: Array<object> }>}
 */
export const buildUnassignedEventsFromAssignerByEmployee = (
  assignerDays = [],
  weekDays = [],
) =>
  weekDays.map((weekDay) => {
    const dateStr = moment(weekDay.date).format("YYYY-MM-DD");
    const dayEntry = assignerDays.find(
      (day) =>
        moment(day?.requestedDate || day?.date).format("YYYY-MM-DD") ===
        dateStr,
    );
    const events = getUnassignedEventsFromAssignerByEmployeeDay(dayEntry);

    return {
      date: weekDay.date,
      dateStr,
      events: sortSchedulerEventsByStartTime(events),
    };
  });

/**
 * @deprecated Use {@link buildUnassignedEventsFromAssignerByEmployee} for staff-by-name weekly view.
 * @param {Array<{ date?: string, schedulerEvents?: Array<object> }>} assignerList
 * @param {Array<{ date?: string }>} weekDays
 * @returns {Array<{ date: string, events: Array<object> }>}
 */
export const buildUnassignedEventsByWeekDays = (assignerList = [], weekDays = []) =>
  weekDays.map((weekDay) => {
    const dateStr = moment(weekDay.date).format("YYYY-MM-DD");
    const dayEntry = assignerList.find(
      (day) => moment(day.date).format("YYYY-MM-DD") === dateStr,
    );
    const events = (dayEntry?.schedulerEvents || []).filter(
      isUnassignedSchedulerEvent,
    );

    return {
      date: weekDay.date,
      dateStr,
      events: sortSchedulerEventsByStartTime(events),
    };
  });

/**
 * Monthly cells should surface unassigned cards before assigned ones.
 * @param {Array<object>} events
 * @returns {Array<object>}
 */
export const sortSchedulerEventsWithUnassignedFirst = (events = []) => {
  const { unassigned, assigned } = partitionSchedulerEvents(events);
  return [
    ...unassigned,
    ...sortSchedulerEventsByStartTime(assigned),
  ];
};

/**
 * Serializes dates for router history state (moment objects cannot be cloned).
 * @param {string | Date | import('moment').Moment | null | undefined} date
 * @returns {string | null | undefined}
 */
export const serializeSchedulerHistoryDate = (date) => {
  if (!date) return date;
  if (typeof date === "string") return date;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

/**
 * Scheduler events may be dragged when the base permission check passes.
 * Draft and unassigned draft events are included.
 * @param {object} eventItem
 * @param {(event: object) => boolean} baseCanDrag
 * @returns {boolean}
 */
export const canDragSchedulerEventWithUnassigned = (eventItem, baseCanDrag) => {
  if (!eventItem) return false;
  return baseCanDrag(eventItem);
};

/**
 * Whether a dragged item may be dropped on the unassigned row.
 * Draft and published events/shifts may move to unassigned from a staff row.
 * @param {object | null | undefined} eventItem
 * @param {{ isUnassigned?: boolean } | null | undefined} sourceContext
 * @returns {boolean}
 */
export const canDropEventToUnassignedSection = (eventItem, sourceContext) => {
  if (!eventItem) return false;

  if (sourceContext?.isUnassigned) {
    return Boolean(eventItem.isDraft) || isUnassignedSchedulerEvent(eventItem);
  }

  return true;
};
